import {
  BuildingTypesEnum,
  ProjectStatusesEnum,
  PropertyValidation,
} from 'dash-common';
import moment from 'moment';
import camelCase from 'lodash/camelCase';
import type { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Flex, Form, FormErrorTypes } from 'dash-ui';

import { type IComponentProps } from 'containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.interfaces';
import { FormFields } from 'containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.constants';
import { StyledBox } from 'containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.styles';
import { FlexWraper } from 'containers/properties/AdvancedPropertyDetails/components/BuildingDetailsFormElements/BuildingDetailsFormElements.style';

const BuildingDetailsFormElements: FC<
  IComponentProps & { disabled?: boolean }
> = ({ formErrors, formHandlers, formState, disabled = false }) => {
  const { t } = useTranslation('forms');
  const validate = (
    field: FormFields,
    value: string,
    { max }: { max: number }
  ) => {
    if (
      [
        FormFields.Interior_Designer,
        FormFields.Landscape_Architect,
        FormFields.Lead_Architect,
        FormFields.Local_Architect,
      ].includes(field) &&
      value.length > max
    ) {
      formHandlers[field].setError({
        type: FormErrorTypes.Invalid,
        message: t('messages.maxCharacterLimit', { number: max }),
      });
      return false;
    }
    formHandlers[field].setError(null);
    return true;
  };

  const handleTextInputs =
    (field: FormFields, { max = 100 }: { max?: number } = {}) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      formHandlers[field].setState(value);
      validate(field, value, { max });
    };

  const handleNumberInputs =
    (field: FormFields) => (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      formHandlers[field].setState(Number(value));
    };

  const handleSelectInputs = (fieldName: FormFields) => (value?: string) => {
    formHandlers[fieldName].setState(value);
  };

  const handleSelectInputsArray = (fieldName: FormFields) => (_?: string, values?: string[]) => {
    formHandlers[fieldName].setState(values);
  };

  return (
    <StyledBox w="100%" pb={6} px={6} mt={6}>
      <Flex>
        <Flex flexDirection="column" w="100%" mr={4}>
          <FlexWraper w="100%" mb={8}>
            <Flex flexDirection="column" mr={4} w="100%">
              <Form.Group w="100%" mb={8}>
                <Form.Label>{t('labels.buildingType')}</Form.Label>
                <Form.Select
                  isMulti
                  menuPortalZIndex="auto"
                  w="100%"
                  h="46px"
                  value={formState?.buildingTypes || ''}
                  options={Object.entries(BuildingTypesEnum).map(
                    ([label, value]) => ({
                      label: t(`labels.buildingTypes.${camelCase(label)}`),
                      value,
                    })
                  )}
                  placeholder={t('placeholders.selectOne')}
                  onChanged={handleSelectInputsArray(FormFields.Building_Types)}
                  disabled={disabled}
                />
              </Form.Group>
              <Form.Group w="100%">
                <Form.Label>{t('labels.projectStatus')}</Form.Label>
                <Form.Select
                  menuPortalZIndex="auto"
                  w="100%"
                  h="46px"
                  value={formState?.projectStatus || ''}
                  options={Object.entries(ProjectStatusesEnum).map(
                    ([label, value]) => ({
                      label: t(`labels.projectStatuses.${camelCase(label)}`),
                      value,
                    })
                  )}
                  placeholder={t('placeholders.selectOne')}
                  onChanged={handleSelectInputs(FormFields.Project_Status)}
                  disabled={disabled}
                />
              </Form.Group>
            </Flex>
            <Flex flexDirection="column" w="100%">
              <Form.Group w="100%" mb={8}>
                <Form.Label>{t('labels.stories')}</Form.Label>
                <Form.Input
                  placeholder={t('placeholders.nbrOfStories') || ''}
                  type="number"
                  value={formState?.stories || ''}
                  onChange={handleNumberInputs(FormFields.Stories)}
                  disabled={disabled}
                />
              </Form.Group>
              <Form.Group w="100%">
                <Form.Label>{t('labels.yearBuilt')}</Form.Label>
                <DatePicker
                  // @ts-ignore
                  disabled={disabled}
                  showYearPicker
                  dateFormat="yyyy"
                  placeholderText={t('placeholders.year') || ''}
                  selected={
                    formState?.yearBuilt
                      ? moment().set('year', formState?.yearBuilt).toDate()
                      : null
                  }
                  onChange={(date: Date) =>
                    formHandlers[FormFields.Year_Built].setState(
                      moment(date).get('year')
                    )
                  }
                />
              </Form.Group>
            </Flex>
          </FlexWraper>
          <Form.Group w="100%" mb={8}>
            <Form.Label>{t('labels.leadArchitect')}</Form.Label>
            <Form.Input
              placeholder={t('placeholders.nameOfArchitect') || ''}
              onChange={handleTextInputs(FormFields.Lead_Architect)}
              value={formState?.leadArchitect || ''}
              $errorType={formErrors[FormFields.Lead_Architect]?.type}
              disabled={disabled}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.Lead_Architect]?.type}
            >
              {formErrors[FormFields.Lead_Architect]?.message}
            </Form.Feedback>
          </Form.Group>
          <Form.Group w="100%" mb={8}>
            <Form.Label>{t('labels.landscapeArchitect')}</Form.Label>
            <Form.Input
              placeholder={t('placeholders.nameOfArchitect') || ''}
              value={formState?.landscapeArchitect || ''}
              onChange={handleTextInputs(FormFields.Landscape_Architect)}
              $errorType={formErrors[FormFields.Landscape_Architect]?.type}
              disabled={disabled}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.Landscape_Architect]?.type}
            >
              {formErrors[FormFields.Landscape_Architect]?.message}
            </Form.Feedback>
          </Form.Group>
        </Flex>
        <Flex flexDirection="column" w="100%">
          <FlexWraper w="100%">
            <Flex w="100%" flexDirection="column" mr={4}>
              <Form.Group w="100%" mb={8}>
                <Form.Label>{t('labels.nbrOfTowers')}</Form.Label>
                <Form.Input
                  disabled={
                    formState?.buildingTypes
                      ? [
                          BuildingTypesEnum.TOWNHOME,
                          BuildingTypesEnum.SINGLE_FAMILY_HOME,
                        ].includes(formState.buildingTypes)
                      : true || disabled
                  }
                  placeholder={t('placeholders.nbrOfTowers') || ''}
                  value={formState?.numberOfTowers || ''}
                  type="number"
                  onChange={handleNumberInputs(FormFields.Number_Of_Towers)}
                />
              </Form.Group>
              {/* <Form.Group w="100%">
                <Form.Label>{t('labels.constructionStartDate')}</Form.Label>
                <DatePicker
                  showYearPicker
                  // @ts-ignore
                  dateFormat="yyyy"
                  placeholderText="YYYY"
                  selected={formState?.constructionStartDate || ''}
                  onChange={(date: Date) =>
                    formHandlers[FormFields.Construction_Start_Date].setState(
                      moment(date).set({ m: 6 }).toDate()
                    )
                  }
                  disabled={disabled}
                />
              </Form.Group> */}
            </Flex>
            <Flex w="100%" flexDirection="column">
              <Form.Group w="100%" mb={8}>
                <Form.Label>{t('labels.nbrOfUnits')}</Form.Label>
                <Form.Input
                  placeholder={t('placeholders.nbrOfUnits') || ''}
                  value={formState?.numberOfUnits || ''}
                  type="number"
                  onChange={handleNumberInputs(FormFields.Number_Of_Units)}
                  disabled={disabled}
                />
              </Form.Group>
              {/* <Form.Group w="100%">
                <Form.Label>{t('labels.completionDate')}</Form.Label>
                <DatePicker
                  showYearPicker
                  // @ts-ignore
                  dateFormat="yyyy"
                  placeholderText="YYYY"
                  selected={formState?.completionDate || ''}
                  onChange={(date: Date) =>
                    formHandlers[FormFields.Completion_Date].setState(
                      moment(date).set({ m: 6 }).toDate()
                    )
                  }
                  disabled={disabled}
                />
              </Form.Group> */}
            </Flex>
          </FlexWraper>

          <Form.Group w="100%" mb={8}>
            <Form.Label>{t('labels.localArchitect')}</Form.Label>
            <Form.Input
              placeholder={t('placeholders.nameOfArchitect') || ''}
              value={formState?.localArchitect || ''}
              onChange={handleTextInputs(FormFields.Local_Architect)}
              $errorType={formErrors[FormFields.Local_Architect]?.type}
              disabled={disabled}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.Local_Architect]?.type}
            >
              {formErrors[FormFields.Local_Architect]?.message}
            </Form.Feedback>
          </Form.Group>
          <Form.Group w="100%" mb={8}>
            <Form.Label>{t('labels.interiorDesigner')}</Form.Label>
            <Form.Input
              placeholder={t('placeholders.nameOfDesigner') || ''}
              value={formState?.interiorDesigner || ''}
              onChange={handleTextInputs(FormFields.Interior_Designer)}
              $errorType={formErrors[FormFields.Interior_Designer]?.type}
              disabled={disabled}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.Interior_Designer]?.type}
            >
              {formErrors[FormFields.Interior_Designer]?.message}
            </Form.Feedback>
          </Form.Group>
          <Form.Group w="100%">
            <Form.Label>{t('labels.taxInformation')}</Form.Label>
            <Form.Input
              onChange={handleTextInputs(FormFields.Tax_Information, {
                max: PropertyValidation.MaxTaxInformationLength,
              })}
              value={formState?.taxInformation || ''}
              placeholder={
                t('placeholders.taxInformationPlaceholder', {
                  number: PropertyValidation.MaxTaxInformationLength,
                }) || ''
              }
              $errorType={formErrors[FormFields.Tax_Information]?.type}
              disabled={disabled}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.Tax_Information]?.type}
            >
              {formErrors[FormFields.Tax_Information]?.message}
            </Form.Feedback>
          </Form.Group>
        </Flex>
      </Flex>
    </StyledBox>
  );
};

export default BuildingDetailsFormElements;
